import { useEffect, useRef, useState } from 'react';
import { fetchAllVehicles } from '../../../lib/vehicleService';
import { Vehicle } from '../../../data/vehicles';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  useEffect(() => {
    fetchAllVehicles()
      .then(setVehicles)
      .catch(console.error);
  }, []);

  const availableCount = vehicles.filter(v => !v.sold).length;
  const uniqueBrands = new Set(vehicles.map(v => v.brand)).size;

  const stats = [
    { number: `${availableCount}+`, label: 'Vehicles In Stock', icon: 'ri-car-line' },
    { number: '50', label: 'States We Ship To', icon: 'ri-map-pin-line' },
    { number: '98%', label: 'Client Satisfaction', icon: 'ri-star-line' },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-12 sm:py-20 lg:py-28 px-4 sm:px-8 lg:px-16 bg-[#111111]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative">
              <div className="relative w-full h-64 sm:h-80 lg:h-[480px] rounded-sm overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/e639cfdc6b45399be78f5734eb4aa46e.jpg"
                  alt="Paramount Motors Team"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              {/* Gold accent border - hidden on mobile */}
              <div className="hidden sm:block absolute -bottom-4 -right-4 w-full h-full border border-[#C9A84C]/30 rounded-sm pointer-events-none"></div>
              {/* Badge - repositioned on mobile */}
              <div className="absolute top-3 left-3 sm:-top-5 sm:-left-5 bg-[#C9A84C] p-3 sm:p-4 lg:p-5 rounded-sm shadow-xl">
                <p
                  className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0d0d0d] leading-none"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  15+
                </p>
                <p
                  className="text-[9px] sm:text-[10px] text-[#0d0d0d]/70 tracking-widest uppercase mt-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Years of Excellence
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
              <div className="h-px w-8 sm:w-10 bg-[#C9A84C]"></div>
              <span
                className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our Story
              </span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Every Vehicle.
              <span className="block text-[#C9A84C]">Every Need. Delivered.</span>
            </h2>
            <p
              className="text-white/70 mb-4 sm:mb-5 leading-relaxed text-xs sm:text-sm"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
            >
              Paramount Motors was built on a simple promise: to connect buyers with the right vehicle, no matter what they need or where they are. Whether you're looking for a luxury sedan, a rugged work truck, a family SUV, or a high-performance sports car — we've got you covered.
            </p>
            <p
              className="text-white/70 mb-6 sm:mb-8 leading-relaxed text-xs sm:text-sm"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
            >
              Every vehicle in our inventory is carefully inspected and presented with full transparency. And if you don't see what you're looking for? We'll source it for you. With nationwide shipping, flexible financing, and a commitment to exceptional service, your perfect vehicle is just a conversation away.
            </p>
            <button
              className="inline-flex items-center gap-2 sm:gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-5 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Learn More About Us
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}